<template>
  <div class="w-full max-w-2xl mx-auto bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden text-left">
    <!-- Header -->
    <div style="background-color: #1e293b;" class="text-white px-6 py-4 flex items-center justify-between">
      <div class="flex items-center space-x-3">
        <div class="w-9 h-9 rounded-lg flex items-center justify-center bg-slate-800 border border-slate-700">
          <i class="fa fa-cash-register text-xl text-yellow-400"></i>
        </div>
        <div>
          <h2 class="text-base md:text-lg font-bold text-white leading-tight">Cobrar Venta - Selección de Moneda(s)</h2>
          <p class="text-xs text-gray-300 mt-0.5">Especifique la moneda o monedas con las que cancela el cliente</p>
        </div>
      </div>
      <button type="button" @click="cancel" class="text-gray-400 hover:text-white text-2xl font-bold leading-none p-1 transition-colors">
        &times;
      </button>
    </div>

    <!-- Cards de Totales -->
    <div class="p-6">
      <div class="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
        <!-- USD -->
        <div class="p-3.5 rounded-xl text-center" style="background-color: #f0fdf4; border: 1.5px solid #86efac;">
          <div class="text-[11px] font-bold uppercase tracking-wider" style="color: #166534;">Total Dólares</div>
          <div class="text-2xl font-extrabold mt-1" style="color: #15803d;">${{ formatNumber(totalUsd) }}</div>
        </div>

        <!-- BS -->
        <div class="p-3.5 rounded-xl text-center" style="background-color: #eff6ff; border: 1.5px solid #93c5fd;">
          <div class="text-[11px] font-bold uppercase tracking-wider" style="color: #1d4ed8;">Total Bolívares</div>
          <div class="text-2xl font-extrabold mt-1" style="color: #1e40af;">Bs. {{ formatNumber(totalBs) }}</div>
        </div>

        <!-- COP (Pesos) -->
        <div class="p-3.5 rounded-xl text-center" style="background-color: #f9fafb; border: 1.5px solid #e5e7eb;">
          <div class="text-[11px] font-bold uppercase tracking-wider" style="color: #4b5563;">Total Pesos (COP)</div>
          <div class="text-2xl font-extrabold mt-1" style="color: #111827;">COP {{ formatNumberCop(totalCop) }}</div>
        </div>
      </div>

      <!-- Selector de Tipo de Pago / Moneda -->
      <div class="mb-5">
        <label class="block text-xs font-bold uppercase text-gray-500 mb-2">¿CÓMO CANCELA EL CLIENTE?</label>
        <div class="grid grid-cols-2 md:grid-cols-4 gap-2">
          <!-- Dólares ($) -->
          <button
            type="button"
            @click="selectCurrency('usd')"
            class="py-2.5 px-3 rounded-lg text-xs md:text-sm flex items-center justify-center space-x-1.5 transition-all cursor-pointer"
            :style="activeCurrency === 'usd' ? 'background-color: #00b074; color: #ffffff; border: 1.5px solid #00b074; font-weight: 700;' : 'background-color: #f3f4f6; color: #374151; border: 1.5px solid #e5e7eb; font-weight: 600;'"
          >
            <i class="fa fa-dollar-sign mr-1"></i>
            <span>Dólares ($)</span>
          </button>

          <!-- Bolívares (Bs) -->
          <button
            type="button"
            @click="selectCurrency('bs')"
            class="py-2.5 px-3 rounded-lg text-xs md:text-sm flex items-center justify-center space-x-1.5 transition-all cursor-pointer"
            :style="activeCurrency === 'bs' ? 'background-color: #00b074; color: #ffffff; border: 1.5px solid #00b074; font-weight: 700;' : 'background-color: #f3f4f6; color: #374151; border: 1.5px solid #e5e7eb; font-weight: 600;'"
          >
            <i class="fa fa-money-bill-wave mr-1"></i>
            <span>Bolívares (Bs)</span>
          </button>

          <!-- Pesos (COP) - DEFAULT -->
          <button
            type="button"
            @click="selectCurrency('cop')"
            class="py-2.5 px-3 rounded-lg text-xs md:text-sm flex items-center justify-center space-x-1.5 transition-all cursor-pointer"
            :style="activeCurrency === 'cop' ? 'background-color: #00b074; color: #ffffff; border: 1.5px solid #00b074; font-weight: 700;' : 'background-color: #f3f4f6; color: #374151; border: 1.5px solid #e5e7eb; font-weight: 600;'"
          >
            <i class="fa fa-coins mr-1"></i>
            <span>Pesos (COP)</span>
          </button>

          <!-- Mixto / Varias -->
          <button
            type="button"
            @click="selectCurrency('mixed')"
            class="py-2.5 px-3 rounded-lg text-xs md:text-sm flex items-center justify-center space-x-1.5 transition-all cursor-pointer"
            :style="activeCurrency === 'mixed' ? 'background-color: #00b074; color: #ffffff; border: 1.5px solid #00b074; font-weight: 700;' : 'background-color: #f3f4f6; color: #374151; border: 1.5px solid #e5e7eb; font-weight: 600;'"
          >
            <i class="fa fa-balance-scale mr-1"></i>
            <span>Mixto / Varias</span>
          </button>
        </div>
      </div>

      <!-- Formulario de Pago según Moneda -->
      <form @submit.prevent="submitPayment">
        <!-- Venta en Pesos COP (Default) -->
        <div v-if="activeCurrency === 'cop'" class="space-y-4">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block text-xs font-semibold text-gray-700 mb-1">Método de Pago en Pesos</label>
              <select v-model="form.method" class="w-full rounded-lg bg-white font-medium" style="border: 1.5px solid #d1d5db; padding: 10px 12px; outline: none;">
                <option value="efectivo">Efectivo Pesos</option>
                <option value="transferencia">Transferencia Bancolombia / Nequi / Daviplata</option>
                <option value="tarjeta">Tarjeta Débito / Crédito</option>
              </select>
            </div>
            <div>
              <label class="block text-xs font-semibold text-gray-700 mb-1">Monto Recibido (COP)</label>
              <input
                type="number"
                step="100"
                min="0"
                v-model.number="receivedAmountCop"
                class="w-full rounded-lg font-bold text-gray-800"
                style="border: 1.5px solid #d1d5db; padding: 10px 12px; outline: none;"
                ref="inputAmountCop"
              />
            </div>
          </div>

          <div v-if="form.method !== 'efectivo'">
            <label class="block text-xs font-semibold text-gray-700 mb-1">Referencia de Pago</label>
            <input
              type="text"
              v-model="form.reference"
              placeholder="Número de comprobante / ref."
              class="w-full rounded-lg"
              style="border: 1.5px solid #d1d5db; padding: 10px 12px; outline: none;"
            />
          </div>

          <!-- Cambio en COP -->
          <div v-if="changeCop > 0" class="p-3 rounded-lg text-center" style="background-color: #f0fdf4; border: 1.5px solid #86efac;">
            <span class="text-sm font-medium" style="color: #166534;">Cambio a devolver:</span>
            <span class="text-lg font-extrabold ml-2" style="color: #15803d;">COP {{ formatNumberCop(changeCop) }}</span>
          </div>
        </div>

        <!-- Venta en USD -->
        <div v-if="activeCurrency === 'usd'" class="space-y-4">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block text-xs font-semibold text-gray-700 mb-1">Método de Pago en USD</label>
              <select v-model="form.method" class="w-full rounded-lg bg-white font-medium" style="border: 1.5px solid #d1d5db; padding: 10px 12px; outline: none;">
                <option value="efectivo">Efectivo Dólar</option>
                <option value="zelle">Zelle</option>
                <option value="transferencia-usd">Transferencia Divisas / Banesco Panamá</option>
              </select>
            </div>
            <div>
              <label class="block text-xs font-semibold text-gray-700 mb-1">Monto Recibido ($ USD)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                v-model.number="receivedAmountUsd"
                class="w-full rounded-lg font-bold text-gray-800"
                style="border: 1.5px solid #d1d5db; padding: 10px 12px; outline: none;"
              />
            </div>
          </div>

          <div v-if="form.method !== 'efectivo'">
            <label class="block text-xs font-semibold text-gray-700 mb-1">Referencia de Pago</label>
            <input
              type="text"
              v-model="form.reference"
              placeholder="Referencia o Titular"
              class="w-full rounded-lg"
              style="border: 1.5px solid #d1d5db; padding: 10px 12px; outline: none;"
            />
          </div>

          <!-- Cambio en USD -->
          <div v-if="changeUsd > 0" class="p-3 rounded-lg text-center" style="background-color: #f0fdf4; border: 1.5px solid #86efac;">
            <span class="text-sm font-medium" style="color: #166534;">Cambio a devolver:</span>
            <span class="text-lg font-extrabold ml-2" style="color: #15803d;">${{ formatNumber(changeUsd) }}</span>
          </div>
        </div>

        <!-- Venta en Bolívares (Bs) -->
        <div v-if="activeCurrency === 'bs'" class="space-y-4">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block text-xs font-semibold text-gray-700 mb-1">Método de Pago en Bolívares</label>
              <select v-model="form.method" class="w-full rounded-lg bg-white font-medium" style="border: 1.5px solid #d1d5db; padding: 10px 12px; outline: none;">
                <option value="pago-movil">Pago Móvil</option>
                <option value="punto-de-venta">Punto de Venta (Tarjeta Débito)</option>
                <option value="efectivo">Efectivo Bolívares</option>
                <option value="transferencia">Transferencia Bs</option>
              </select>
            </div>
            <div>
              <label class="block text-xs font-semibold text-gray-700 mb-1">Monto Recibido (Bs)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                v-model.number="receivedAmountBs"
                class="w-full rounded-lg font-bold text-gray-800"
                style="border: 1.5px solid #d1d5db; padding: 10px 12px; outline: none;"
              />
            </div>
          </div>

          <div v-if="form.method !== 'efectivo'">
            <label class="block text-xs font-semibold text-gray-700 mb-1">Referencia</label>
            <input
              type="text"
              v-model="form.reference"
              placeholder="Número de referencia"
              class="w-full rounded-lg"
              style="border: 1.5px solid #d1d5db; padding: 10px 12px; outline: none;"
            />
          </div>

          <!-- Cambio en Bs -->
          <div v-if="changeBs > 0" class="p-3 rounded-lg text-center" style="background-color: #f0fdf4; border: 1.5px solid #86efac;">
            <span class="text-sm font-medium" style="color: #166534;">Cambio a devolver:</span>
            <span class="text-lg font-extrabold ml-2" style="color: #15803d;">Bs. {{ formatNumber(changeBs) }}</span>
          </div>
        </div>

        <!-- Venta Mixta / Varias Monedas (Con Tasa Promedio Peso/Bolívar) -->
        <div v-if="activeCurrency === 'mixed'" class="space-y-4">
          
          <!-- Banner Informativo de Tasa Promedio -->
          <div class="p-3 rounded-xl flex items-center justify-between" style="background-color: #f0fdf4; border: 1.5px solid #86efac;">
            <div>
              <div class="text-xs font-extrabold uppercase" style="color: #166534;">
                <i class="fa fa-calculator mr-1"></i> Tasa Promedio Peso / Bolívar:
                <span class="text-sm font-black ml-1 text-emerald-800">{{ formatRate(averageRateCopBs) }} COP/Bs</span>
              </div>
              <div class="text-[11px] text-emerald-700 mt-0.5">
                Calculada de: COP {{ formatNumberCop(totalCop) }} ÷ Bs. {{ formatNumber(totalBs) }}
              </div>
            </div>
            <div class="text-right text-[11px] text-gray-500 font-medium">
              Tasa BCV: Bs. {{ formatNumber(change) }}
            </div>
          </div>

          <!-- Formulario para Agregar Abono -->
          <div class="p-4 rounded-xl bg-gray-50 border border-gray-200">
            <div class="text-xs font-bold uppercase text-gray-700 mb-3 flex items-center">
              <i class="fa fa-plus-circle mr-1.5 text-blue-600"></i>
              <span>Registrar Abono / Pago</span>
            </div>

            <!-- Selector de Moneda del Abono -->
            <div class="grid grid-cols-3 gap-2 mb-3">
              <button
                type="button"
                @click="setMixedCurrency('cop')"
                class="py-2 px-2 rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center justify-center space-x-1"
                :style="mixedItem.currency === 'cop' ? 'background-color: #00b074; color: #ffffff;' : 'background-color: #ffffff; color: #4b5563; border: 1px solid #d1d5db;'"
              >
                <i class="fa fa-coins mr-1"></i>
                <span>Pesos (COP)</span>
              </button>
              <button
                type="button"
                @click="setMixedCurrency('bs')"
                class="py-2 px-2 rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center justify-center space-x-1"
                :style="mixedItem.currency === 'bs' ? 'background-color: #00b074; color: #ffffff;' : 'background-color: #ffffff; color: #4b5563; border: 1px solid #d1d5db;'"
              >
                <i class="fa fa-money-bill-wave mr-1"></i>
                <span>Bolívares (Bs)</span>
              </button>
              <button
                type="button"
                @click="setMixedCurrency('usd')"
                class="py-2 px-2 rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center justify-center space-x-1"
                :style="mixedItem.currency === 'usd' ? 'background-color: #00b074; color: #ffffff;' : 'background-color: #ffffff; color: #4b5563; border: 1px solid #d1d5db;'"
              >
                <i class="fa fa-dollar-sign mr-1"></i>
                <span>Dólares ($)</span>
              </button>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
              <!-- Método de Pago -->
              <div>
                <label class="block text-xs font-semibold text-gray-600 mb-1">Método</label>
                <select v-model="mixedItem.method" class="w-full rounded-lg p-2 text-xs font-medium bg-white" style="border: 1px solid #d1d5db; outline: none;">
                  <template v-if="mixedItem.currency === 'cop'">
                    <option value="efectivo">Efectivo Pesos</option>
                    <option value="transferencia">Transferencia / Nequi / Bancolombia</option>
                    <option value="tarjeta">Tarjeta COP</option>
                  </template>
                  <template v-else-if="mixedItem.currency === 'bs'">
                    <option value="pago-movil">Pago Móvil</option>
                    <option value="punto-de-venta">Punto de Venta</option>
                    <option value="efectivo">Efectivo Bs</option>
                    <option value="transferencia">Transferencia Bs</option>
                  </template>
                  <template v-else>
                    <option value="efectivo">Efectivo Dólar</option>
                    <option value="zelle">Zelle</option>
                    <option value="transferencia-usd">Transferencia Divisas</option>
                  </template>
                </select>
              </div>

              <!-- Monto -->
              <div>
                <label class="block text-xs font-semibold text-gray-600 mb-1">
                  Monto ({{ mixedItem.currency.toUpperCase() }})
                </label>
                <input
                  type="number"
                  :step="mixedItem.currency === 'cop' ? '100' : '0.01'"
                  min="0.01"
                  v-model.number="mixedItem.amount"
                  class="w-full rounded-lg p-2 text-xs font-bold text-gray-800"
                  style="border: 1.5px solid #d1d5db; outline: none;"
                  placeholder="0.00"
                />
              </div>

              <!-- Referencia -->
              <div>
                <label class="block text-xs font-semibold text-gray-600 mb-1">Referencia (Opcional)</label>
                <input
                  type="text"
                  v-model="mixedItem.reference"
                  placeholder="Comprobante / ref."
                  class="w-full rounded-lg p-2 text-xs"
                  style="border: 1px solid #d1d5db; outline: none;"
                />
              </div>
            </div>

            <!-- Preview en Vivo de la Conversión (Matemática de Libro1.xlsx) -->
            <div v-if="mixedItem.amount > 0" class="mt-3 p-2.5 rounded-lg bg-blue-50 border border-blue-200 text-xs">
              <div class="font-bold text-blue-900 mb-1">
                <i class="fa fa-info-circle mr-1 text-blue-700"></i> Conversión de este abono:
              </div>
              <div class="grid grid-cols-2 md:grid-cols-3 gap-2 text-blue-800">
                <div v-if="mixedItem.currency === 'bs'">
                  <strong>En Pesos:</strong> COP {{ formatNumberCop(mixedItem.amount * averageRateCopBs) }}
                </div>
                <div v-if="mixedItem.currency === 'bs'">
                  <strong>En USD:</strong> ${{ formatNumber(change > 0 ? mixedItem.amount / change : 0) }}
                </div>

                <div v-if="mixedItem.currency === 'cop'">
                  <strong>En Bs:</strong> Bs. {{ formatNumber(averageRateCopBs > 0 ? mixedItem.amount / averageRateCopBs : 0) }}
                </div>
                <div v-if="mixedItem.currency === 'cop'">
                  <strong>En USD:</strong> ${{ formatNumber(cop_change > 0 ? mixedItem.amount / cop_change : 0) }}
                </div>

                <div v-if="mixedItem.currency === 'usd'">
                  <strong>En Bs:</strong> Bs. {{ formatNumber(mixedItem.amount * change) }}
                </div>
                <div v-if="mixedItem.currency === 'usd'">
                  <strong>En Pesos:</strong> COP {{ formatNumberCop(mixedItem.amount * change * averageRateCopBs) }}
                </div>
              </div>
            </div>

            <!-- Botón Agregar Abono -->
            <div class="mt-3 flex justify-end">
              <button
                type="button"
                @click="addMixedPayment"
                class="py-2 px-4 rounded-lg text-xs font-bold text-white shadow transition-all cursor-pointer flex items-center space-x-1.5"
                style="background-color: #2563eb;"
              >
                <i class="fa fa-plus"></i>
                <span>Añadir Abono a la Lista</span>
              </button>
            </div>
          </div>

          <!-- Tabla de Abonos Realizados -->
          <div v-if="mixedPayments.length > 0" class="mt-4">
            <div class="text-xs font-bold uppercase text-gray-700 mb-2">Abonos Registrados</div>
            <div class="overflow-x-auto rounded-lg border border-gray-200 bg-white">
              <table class="w-full text-left text-xs">
                <thead class="bg-gray-100 text-gray-600 font-bold border-b border-gray-200">
                  <tr>
                    <th class="p-2.5">Moneda / Método</th>
                    <th class="p-2.5 text-right">Monto Original</th>
                    <th class="p-2.5 text-right">Equiv. Bs</th>
                    <th class="p-2.5 text-right">Equiv. Pesos</th>
                    <th class="p-2.5 text-right">Equiv. USD</th>
                    <th class="p-2.5 text-center">Acción</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-gray-100">
                  <tr v-for="(p, idx) in mixedPayments" :key="idx" class="hover:bg-gray-50">
                    <td class="p-2.5 font-bold uppercase text-gray-800">
                      <span class="px-1.5 py-0.5 rounded text-[10px] mr-1" :style="p.currency === 'cop' ? 'background-color: #fef3c7; color: #92400e;' : (p.currency === 'bs' ? 'background-color: #dbeafe; color: #1e40af;' : 'background-color: #dcfce7; color: #166534;')">
                        {{ p.currency }}
                      </span>
                      {{ p.method }}
                      <span v-if="p.reference" class="text-gray-400 font-normal text-[10px] block">Ref: {{ p.reference }}</span>
                    </td>
                    <td class="p-2.5 text-right font-extrabold text-gray-900">
                      {{ p.currency === 'cop' ? 'COP ' + formatNumberCop(p.amount) : (p.currency === 'bs' ? 'Bs. ' + formatNumber(p.amount) : '$' + formatNumber(p.amount)) }}
                    </td>
                    <td class="p-2.5 text-right text-blue-700 font-semibold">
                      Bs. {{ formatNumber(p.amount_bs) }}
                    </td>
                    <td class="p-2.5 text-right text-gray-700 font-semibold">
                      COP {{ formatNumberCop(p.amount_cop) }}
                    </td>
                    <td class="p-2.5 text-right text-green-700 font-semibold">
                      ${{ formatNumber(p.amount_usd) }}
                    </td>
                    <td class="p-2.5 text-center">
                      <button type="button" @click="removeMixedPayment(idx)" class="text-red-500 hover:text-red-700 p-1 transition-colors" title="Eliminar abono">
                        <i class="fa fa-trash"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Resumen de Resta (Saldo Pendiente) -->
          <div class="p-4 rounded-xl" :style="remainingUsd <= 0.05 ? 'background-color: #f0fdf4; border: 2px solid #86efac;' : 'background-color: #fef2f2; border: 2px solid #fecaca;'">
            <div class="flex items-center justify-between mb-2">
              <span class="text-xs font-extrabold uppercase flex items-center" :style="remainingUsd <= 0.05 ? 'color: #166534;' : 'color: #991b1b;'">
                <i v-if="remainingUsd <= 0.05" class="fa fa-check-circle mr-1.5 text-green-600"></i>
                <i v-else class="fa fa-hourglass-half mr-1.5 text-red-600"></i>
                <span>{{ remainingUsd <= 0.05 ? 'Totalmente Pagado / Cuadrado' : 'Saldo Restante / Pendiente:' }}</span>
              </span>
              <span v-if="remainingUsd <= 0.05" class="px-2 py-0.5 rounded-full text-xs font-bold text-white bg-green-600">
                Listo para Facturar
              </span>
            </div>
            <div class="grid grid-cols-3 gap-2 text-center">
              <div class="p-2 rounded-lg bg-white shadow-sm">
                <div class="text-[10px] font-bold text-gray-500 uppercase">Resta Dólares</div>
                <div class="text-base font-extrabold text-green-700">${{ formatNumber(remainingUsd) }}</div>
              </div>
              <div class="p-2 rounded-lg bg-white shadow-sm">
                <div class="text-[10px] font-bold text-gray-500 uppercase">Resta Bolívares</div>
                <div class="text-base font-extrabold text-blue-700">Bs. {{ formatNumber(remainingBs) }}</div>
              </div>
              <div class="p-2 rounded-lg bg-white shadow-sm">
                <div class="text-[10px] font-bold text-gray-500 uppercase">Resta Pesos (COP)</div>
                <div class="text-base font-extrabold text-gray-900">COP {{ formatNumberCop(remainingCop) }}</div>
              </div>
            </div>
          </div>

        </div>

        <!-- Botones de Acción -->
        <div class="mt-6 pt-4 border-t border-gray-200 flex items-center justify-between">
          <button
            type="button"
            @click="cancel"
            class="px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors cursor-pointer"
            style="background-color: #e5e7eb; color: #374151; border: 1px solid #d1d5db;"
          >
            Cancelar
          </button>
          <button
            type="submit"
            :disabled="loading || (activeCurrency === 'mixed' && mixedPayments.length === 0)"
            class="px-6 py-3 rounded-lg text-sm md:text-base font-bold text-white transition-all transform hover:scale-[1.02] flex items-center space-x-2 cursor-pointer shadow-lg"
            :style="(activeCurrency === 'mixed' && remainingUsd > 0.05) ? 'background-color: #f59e0b;' : 'background-color: #00b074;'"
          >
            <i class="fa fa-check"></i>
            <span v-if="activeCurrency === 'mixed' && remainingUsd > 0.05">Confirmar Pago Parcial [F9]</span>
            <span v-else>Confirmar y Finalizar Venta [F9]</span>
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PaymentBox',

  props: {
    order: {
      type: Object,
      required: true
    },
    payment_methods: {
      type: Object,
      default: () => ({})
    },
    change: {
      type: Number,
      default: 1
    },
    cop_change: {
      type: Number,
      default: 1
    },
    cancellable: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      activeCurrency: 'cop', // DEFAULT: PESOS COP
      receivedAmountCop: 0,
      receivedAmountUsd: 0,
      receivedAmountBs: 0,
      mixedPayments: [],
      mixedItem: {
        currency: 'cop',
        method: 'efectivo',
        amount: '',
        reference: ''
      },
      form: this.$inertia.form({
        method: 'efectivo',
        amount: 0,
        amount_bs: 0,
        amount_cop: 0,
        currency: 'cop',
        order_id: null,
        reference: '',
        quick: true,
        payments: []
      }),
      loading: false
    }
  },

  computed: {
    totalUsd() {
      const debt = this.order.total - (this.order.balance || 0)
      return debt > 0 ? parseFloat(debt.toFixed(2)) : 0
    },

    totalBs() {
      const bs = this.totalUsd * (this.change || 1)
      return parseFloat(bs.toFixed(2))
    },

    totalCop() {
      if (this.order.total_cop && parseFloat(this.order.total_cop) > 0) {
        return Math.round(parseFloat(this.order.total_cop) / 100) * 100
      }
      const rawCop = this.totalUsd * (this.cop_change || 1)
      return Math.round(rawCop / 100) * 100
    },

    // Tasa Promedio Peso / Bolívar: Total Pesos / Total Bs (Exacto de Libro1.xlsx)
    averageRateCopBs() {
      if (this.totalBs > 0) {
        return parseFloat((this.totalCop / this.totalBs).toFixed(4))
      }
      return this.change > 0 ? parseFloat(((this.cop_change || 1) / this.change).toFixed(4)) : 1
    },

    changeCop() {
      return this.receivedAmountCop > this.totalCop ? this.receivedAmountCop - this.totalCop : 0
    },

    changeUsd() {
      return this.receivedAmountUsd > this.totalUsd ? this.receivedAmountUsd - this.totalUsd : 0
    },

    changeBs() {
      return this.receivedAmountBs > this.totalBs ? this.receivedAmountBs - this.totalBs : 0
    },

    // Acumulado de pagos mixtos
    mixedPaidUsd() {
      return this.mixedPayments.reduce((acc, p) => acc + (parseFloat(p.amount_usd) || 0), 0)
    },

    mixedPaidBs() {
      return this.mixedPayments.reduce((acc, p) => acc + (parseFloat(p.amount_bs) || 0), 0)
    },

    mixedPaidCop() {
      return this.mixedPayments.reduce((acc, p) => acc + (parseFloat(p.amount_cop) || 0), 0)
    },

    // Resta / Saldo Pendiente
    remainingUsd() {
      const rem = this.totalUsd - this.mixedPaidUsd
      return rem > 0.005 ? parseFloat(rem.toFixed(2)) : 0
    },

    remainingBs() {
      const rem = this.totalBs - this.mixedPaidBs
      return rem > 0.005 ? parseFloat(rem.toFixed(2)) : 0
    },

    remainingCop() {
      const rem = this.totalCop - this.mixedPaidCop
      return rem > 1 ? Math.round(rem) : 0
    }
  },

  mounted() {
    this.selectCurrency('cop')

    // Atajo de teclado F9
    window.addEventListener('keydown', this.handleKeyDown)
  },

  beforeUnmount() {
    window.removeEventListener('keydown', this.handleKeyDown)
  },

  methods: {
    handleKeyDown(e) {
      if (e.key === 'F9' || e.keyCode === 120) {
        e.preventDefault()
        this.submitPayment()
      }
    },

    selectCurrency(curr) {
      this.activeCurrency = curr
      this.form.currency = curr

      if (curr === 'cop') {
        this.form.method = 'efectivo'
        this.receivedAmountCop = this.totalCop
        this.form.amount_cop = this.totalCop
        this.form.amount = this.totalUsd
        this.form.amount_bs = this.totalBs
      } else if (curr === 'usd') {
        this.form.method = 'efectivo'
        this.receivedAmountUsd = this.totalUsd
        this.form.amount = this.totalUsd
        this.form.amount_bs = this.totalBs
        this.form.amount_cop = this.totalCop
      } else if (curr === 'bs') {
        this.form.method = 'pago-movil'
        this.receivedAmountBs = this.totalBs
        this.form.amount_bs = this.totalBs
        this.form.amount = this.totalUsd
        this.form.amount_cop = this.totalCop
      } else if (curr === 'mixed') {
        this.mixedPayments = []
        this.setMixedCurrency('cop')
      }
    },

    setMixedCurrency(curr) {
      this.mixedItem.currency = curr
      if (curr === 'cop') {
        this.mixedItem.method = 'efectivo'
        this.mixedItem.amount = this.remainingCop > 0 ? this.remainingCop : ''
      } else if (curr === 'bs') {
        this.mixedItem.method = 'pago-movil'
        this.mixedItem.amount = this.remainingBs > 0 ? this.remainingBs : ''
      } else if (curr === 'usd') {
        this.mixedItem.method = 'efectivo'
        this.mixedItem.amount = this.remainingUsd > 0 ? this.remainingUsd : ''
      }
    },

    addMixedPayment() {
      const rawAmount = parseFloat(this.mixedItem.amount)
      if (!rawAmount || rawAmount <= 0) return

      let amountUsd = 0
      let amountBs = 0
      let amountCop = 0

      const avgRate = this.averageRateCopBs || 1
      const bcvRate = this.change || 1
      const copRate = this.cop_change || 1

      if (this.mixedItem.currency === 'bs') {
        amountBs = rawAmount
        amountCop = parseFloat((rawAmount * avgRate).toFixed(2))
        amountUsd = bcvRate > 0 ? parseFloat((rawAmount / bcvRate).toFixed(4)) : 0
      } else if (this.mixedItem.currency === 'cop') {
        amountCop = rawAmount
        amountBs = avgRate > 0 ? parseFloat((rawAmount / avgRate).toFixed(2)) : 0
        amountUsd = copRate > 0 ? parseFloat((rawAmount / copRate).toFixed(4)) : 0
      } else if (this.mixedItem.currency === 'usd') {
        amountUsd = rawAmount
        amountBs = parseFloat((rawAmount * bcvRate).toFixed(2))
        amountCop = parseFloat((amountBs * avgRate).toFixed(2))
      }

      this.mixedPayments.push({
        currency: this.mixedItem.currency,
        method: this.mixedItem.method,
        amount: rawAmount,
        amount_usd: amountUsd,
        amount_bs: amountBs,
        amount_cop: amountCop,
        reference: this.mixedItem.reference || ''
      })

      // Limpiar y sugerir siguiente abono con el restante
      this.mixedItem.reference = ''
      this.$nextTick(() => {
        if (this.remainingCop > 0) {
          this.setMixedCurrency('cop')
        } else if (this.remainingBs > 0) {
          this.setMixedCurrency('bs')
        } else {
          this.mixedItem.amount = ''
        }
      })
    },

    removeMixedPayment(index) {
      this.mixedPayments.splice(index, 1)
      this.$nextTick(() => {
        this.setMixedCurrency(this.mixedItem.currency)
      })
    },

    formatNumber(val) {
      return new Intl.NumberFormat('es-VE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(val || 0)
    },

    formatRate(val) {
      return new Intl.NumberFormat('es-VE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 4
      }).format(val || 0)
    },

    formatNumberCop(val) {
      return new Intl.NumberFormat('es-CO', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(val || 0)
    },

    cancel() {
      this.$emit('cancel')
      if (!this.cancellable) {
        window.location.href = '/pos'
      }
    },

    submitPayment() {
      if (this.loading) return
      this.loading = true

      this.form.order_id = this.order.id

      if (this.activeCurrency === 'cop') {
        this.form.currency = 'cop'
        this.form.amount = this.totalUsd
        this.form.amount_cop = this.totalCop
        this.form.amount_bs = this.totalBs
        this.form.payments = []
      } else if (this.activeCurrency === 'usd') {
        this.form.currency = 'usd'
        this.form.amount = this.totalUsd
        this.form.amount_cop = this.totalCop
        this.form.amount_bs = this.totalBs
        this.form.payments = []
      } else if (this.activeCurrency === 'bs') {
        this.form.currency = 'bs'
        this.form.amount = this.totalUsd
        this.form.amount_bs = this.totalBs
        this.form.amount_cop = this.totalCop
        this.form.payments = []
      } else if (this.activeCurrency === 'mixed') {
        if (this.mixedPayments.length === 0) {
          this.loading = false
          alert('Por favor agregue al menos un abono en la lista.')
          return
        }
        this.form.currency = 'mixed'
        this.form.amount = this.mixedPaidUsd
        this.form.amount_bs = this.mixedPaidBs
        this.form.amount_cop = this.mixedPaidCop
        this.form.payments = this.mixedPayments
      }

      this.form.post('/orders/pay', {
        onSuccess: () => {
          this.loading = false
        },
        onError: () => {
          this.loading = false
          alert('Ocurrió un error al procesar el pago.')
        }
      })
    }
  }
}
</script>

